import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Tag,
  Tabs,
  Table,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Typography,
  Row,
  Col,
  Statistic,
  Tooltip,
  Popconfirm,
  Avatar,
  List,
  Spin
} from 'antd';
import ARKItemSelector from '../../components/ARKItemSelector';
import PlayerSelector from '../../components/PlayerSelector';
import {
  UserOutlined,
  TeamOutlined,
  TrophyOutlined,
  MessageOutlined,
  GiftOutlined,
  DeleteOutlined,
  SendOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  RobotOutlined,
  RocketOutlined,
  BarChartOutlined,
  BugOutlined,
  CodeOutlined,
  ConsoleSqlOutlined,
  BookOutlined,
  ReloadOutlined,
  CloudOutlined,
  SaveOutlined,
  ClockCircleOutlined,
  FireOutlined,
  DollarOutlined,
  PlusOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

// 玩家接口
interface Player {
  id: number;
  name: string;
  steamId: string;
  playerId: string;
  level: number;
  tribe: string;
  tribeId?: string;
  tribeName?: string;
  isOnline: boolean;
  lastOnline: string;
  position?: {
    x: number;
    y: number;
    z: number;
    formatted: string;
  } | string;
  dinoCount?: number;
  health?: number;
  stamina?: number;
  food?: number;
  water?: number;
  weight?: number;
  oxygen?: number;
  craftingSpeed?: number;
  fortitude?: number;
  movementSpeed?: number;
  meleeDamage?: number;
  speed?: number;
  points?: number; // 玩家金币
}

// 服务器统计接口
interface ServerStats {
  onlinePlayers: number;
  totalTames: number;
  totalWilds: number;
  unclaimedDinos: number;
  serverFrames: number;
  uptime: string;
  memoryUsage: number;
  cpuUsage: number;
}

// 聊天消息接口
interface ChatMessage {
  id: string;
  type: 'player' | 'system' | 'admin';
  player: string;
  message: string;
  timestamp: string;
  time?: string;
}

// 命令历史接口
interface CommandHistory {
  id: string;
  command: string;
  timestamp: string;
  success: boolean;
  result?: string;
}

interface ARKRCONConsoleProps {
  serverId?: string;
}

const ARKRCONConsole: React.FC<ARKRCONConsoleProps> = ({ serverId: propServerId }) => {
  const { serverId: paramServerId } = useParams<{ serverId: string }>();
  const navigate = useNavigate();
  const serverId = propServerId || paramServerId;
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [serverStats, setServerStats] = useState<ServerStats | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [commandHistory, setCommandHistory] = useState<CommandHistory[]>([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [queryResult, setQueryResult] = useState<string>('');
  const [queryResultModalVisible, setQueryResultModalVisible] = useState(false);
  const [queryResultTitle, setQueryResultTitle] = useState<string>('');
  const [serverStatus, setServerStatus] = useState<string>('unknown');
  const [serverStatusModalVisible, setServerStatusModalVisible] = useState(false);
  
  // 模态框状态
  const [commandModalVisible, setCommandModalVisible] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState<any>(null);
  const [commandForm] = Form.useForm();
  
  // 物品选择器状态（已移除，使用ARKItemSelector组件）
  const [teleportModalVisible, setTeleportModalVisible] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [teleportForm] = Form.useForm();
  const [giveItemModalVisible, setGiveItemModalVisible] = useState(false);
  const [giveItemForm] = Form.useForm();
  const [giveExpModalVisible, setGiveExpModalVisible] = useState(false);
  const [giveExpForm] = Form.useForm();
  const [chatModalVisible, setChatModalVisible] = useState(false);
  const [chatForm] = Form.useForm();
  const [weatherModalVisible, setWeatherModalVisible] = useState(false);
  const [weatherForm] = Form.useForm();
  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [renameForm] = Form.useForm();
  const [addPointsModalVisible, setAddPointsModalVisible] = useState(false);
  const [addPointsForm] = Form.useForm();
  const [setPointsModalVisible, setSetPointsModalVisible] = useState(false);
  const [setPointsForm] = Form.useForm();
  
  // 插件检查相关状态
  const [pluginCheckModalVisible, setPluginCheckModalVisible] = useState(false);
  const [isYouYouNiaoRCONInstalled, setIsYouYouNiaoRCONInstalled] = useState(false);

  // 命令输入
  const [currentCommand, setCurrentCommand] = useState('');
  const [commandHistoryIndex, setCommandHistoryIndex] = useState(-1);
  const commandInputRef = useRef<any>(null);

  // 检查服务器状态
  const checkServerStatus = useCallback(async () => {
    if (!serverId) return;
    
    try {
      const response = await api.get(`/servers/${serverId}`);
      const status = response.data?.status;
      setServerStatus(status || 'unknown');
      
      // 如果服务器未运行，显示弹窗提示
      if (status && status !== 'running') {
        setServerStatusModalVisible(true);
      }
      
      return status;
    } catch (error) {
      console.error('检查服务器状态失败:', error);
      setServerStatus('unknown');
      return 'unknown';
    }
  }, [serverId]);

  // 检查YouYouNiaoRCON插件是否安装
  const checkYouYouNiaoRCONPlugin = useCallback(async () => {
    if (!serverId) return;
    
    try {
      const response = await api.get(`/plugins/${serverId}/plugins`);
      const plugins = response.data?.data?.plugins || [];
      
      // 查找YouYouNiaoRCON插件
      const youYouNiaoRCONPlugin = plugins.find((plugin: any) => 
        plugin.name === 'YouYouNiaoRCON' && plugin.isInstalled
      );
      
      const isInstalled = !!youYouNiaoRCONPlugin;
      setIsYouYouNiaoRCONInstalled(isInstalled);
      
      // 如果未安装，显示警告弹窗
      if (!isInstalled) {
        setPluginCheckModalVisible(true);
      }
      
      return isInstalled;
    } catch (error) {
      console.error('检查YouYouNiaoRCON插件失败:', error);
      // 如果检查失败，假设未安装并显示警告
      setIsYouYouNiaoRCONInstalled(false);
      setPluginCheckModalVisible(true);
      return false;
    }
  }, [serverId]);

  // 跳转到插件管理页面
  const navigateToPluginManagement = useCallback(() => {
    if (serverId) {
      // 跳转到服务器详情页面的插件管理标签
      navigate(`/servers/${serverId}?tab=plugins`);
    }
  }, [serverId, navigate]);

  // 加载数据
  // 获取玩家PlayerID
  const getPlayerID = useCallback(async (steamId: string): Promise<string> => {
    try {
      const response = await api.post(`/ark-rcon/servers/${serverId}/execute`, {
        command: 'GetPlayerIDForSteamID',
        params: [steamId]
      });
      
      if (response.data.success) {
        // 解析返回的PlayerID
        const result = response.data.result;
        console.log('GetPlayerIDForSteamID 返回结果:', result);
        
        // 处理不同的返回格式
        // 格式1: "SteamID: 0, ??ID: 2147483647"
        // 格式2: "PlayerID: 123456"
        // 格式3: 直接是数字
        
        // 尝试匹配 ??ID: 数字 格式
        let match = result.match(/\?\?ID:\s*(\d+)/);
        if (match) {
          console.log('匹配到??ID格式:', match[1]);
          return match[1];
        }
        
        // 尝试匹配 PlayerID: 数字 格式
        match = result.match(/PlayerID:\s*(\d+)/);
        if (match) {
          return match[1];
        }
        
        // 尝试匹配任何数字
        match = result.match(/(\d+)/);
        if (match) {
          return match[1];
        }
        
        return result;
      }
      return '';
    } catch (error) {
      console.error('获取PlayerID失败:', error);
      return '';
    }
  }, [serverId]);

  // 获取玩家金币
  const getPlayerPoints = useCallback(async (steamId: string): Promise<number> => {
    try {
      const response = await api.post(`/ark-rcon/servers/${serverId}/execute`, {
        command: 'GetPlayerPoints',
        params: [steamId]
      });
      
      if (response.data.success) {
        const result = response.data.result;
        console.log('GetPlayerPoints 返回结果:', result);
        
        // 尝试从结果中提取数字
        const match = result.match(/(\d+)/);
        if (match) {
          return parseInt(match[1], 10);
        }
        
        return 0;
      }
      return 0;
    } catch (error) {
      console.error('获取玩家金币失败:', error);
      return 0;
    }
  }, [serverId]);

  // 加载玩家列表
  const loadPlayers = useCallback(async () => {
    if (!serverId) return;
    
    // 先检查服务器状态
    const status = await checkServerStatus();
    if (status && status !== 'running') {
      setLoading(false);
      return; // 如果服务器未运行，不加载玩家数据
    }
    
    setLoading(true);
    try {
      const playersRes = await api.get(`/ark-rcon/servers/${serverId}/players`);
      const playersData = playersRes.data?.players || [];
      
      // 为每个玩家获取PlayerID和金币
      const playersWithPlayerID = await Promise.all(
        playersData.map(async (player: Player) => {
          let updatedPlayer = { ...player };
          
          // 总是尝试获取PlayerID，除非已经有有效的PlayerID
          if (!player.playerId || player.playerId === '0' || player.playerId === '') {
            console.log(`正在获取玩家 ${player.name} (${player.steamId}) 的PlayerID...`);
            const playerID = await getPlayerID(player.steamId);
            console.log(`玩家 ${player.name} 的PlayerID: ${playerID}`);
            updatedPlayer.playerId = playerID;
          } else {
            console.log(`玩家 ${player.name} 已有PlayerID: ${player.playerId}`);
          }
          
          // 获取玩家金币
          console.log(`正在获取玩家 ${player.name} (${player.steamId}) 的金币...`);
          const points = await getPlayerPoints(player.steamId);
          console.log(`玩家 ${player.name} 的金币: ${points}`);
          updatedPlayer.points = points;
          
          return updatedPlayer;
        })
      );
      
      setPlayers(playersWithPlayerID);
      message.success('玩家列表刷新成功');
    } catch (error) {
      console.error('加载玩家列表失败:', error);
      message.error('加载玩家列表失败');
    } finally {
      setLoading(false);
    }
  }, [serverId, getPlayerID, getPlayerPoints, checkServerStatus]);

  // 加载聊天消息
  const loadChatMessages = useCallback(async () => {
    if (!serverId) return;
    
    try {
      const chatRes = await api.get(`/ark-rcon/servers/${serverId}/chat`);
      const bufferMessages = chatRes.data?.messages || [];
      
      if (bufferMessages.length > 0) {
        // GetChat返回的是聊天缓冲区，不是完整历史
        // 我们需要智能合并：保留历史消息，添加缓冲区中的新消息
        setChatMessages(prevMessages => {
          const existingIds = new Set(prevMessages.map(msg => msg.id));
          const newMessages = bufferMessages.filter((msg: ChatMessage) => !existingIds.has(msg.id));
          
          if (newMessages.length > 0) {
            message.success(`获取到 ${newMessages.length} 条新聊天记录`);
            return [...prevMessages, ...newMessages].sort((a, b) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
          } else {
            // 缓冲区中没有新消息，但可能有更新的消息
            // 检查是否有时间戳更新的消息
            const updatedMessages = bufferMessages.filter((bufferMsg: ChatMessage) => {
              const existingMsg = prevMessages.find(prevMsg => prevMsg.id === bufferMsg.id);
              return existingMsg && new Date(bufferMsg.timestamp) > new Date(existingMsg.timestamp);
            });
            
            if (updatedMessages.length > 0) {
              message.info(`更新了 ${updatedMessages.length} 条聊天记录`);
              // 更新现有消息
              const updatedList = prevMessages.map(prevMsg => {
                const updatedMsg = updatedMessages.find((updMsg: ChatMessage) => updMsg.id === prevMsg.id);
                return updatedMsg || prevMsg;
              });
              return updatedList.sort((a, b) => 
                new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
              );
            } else {
              message.info('聊天缓冲区没有新内容');
              return prevMessages;
            }
          }
        });
      } else {
        message.info('聊天缓冲区为空（可能没有新消息或缓冲区已清空）');
      }
    } catch (error) {
      console.error('加载聊天消息失败:', error);
      message.error('加载聊天消息失败');
    }
  }, [serverId]);

  // 商店物品加载逻辑已移除，使用ARKItemSelector组件

  // 物品选择器相关函数已移除，使用ARKItemSelector组件

  const loadData = useCallback(async () => {
    if (!serverId) return;
    
    // 先检查服务器状态
    const status = await checkServerStatus();
    if (status && status !== 'running') {
      setLoading(false);
      return; // 如果服务器未运行，不加载RCON数据
    }
    
    setLoading(true);
    try {
      // 并行加载所有数据
      const [playersRes, statsRes, chatRes] = await Promise.all([
        api.get(`/ark-rcon/servers/${serverId}/players`),
        api.get(`/ark-rcon/servers/${serverId}/stats`),
        api.get(`/ark-rcon/servers/${serverId}/chat`)
      ]);
      
      const playersData = playersRes.data?.players || [];
      
      // 为每个玩家获取PlayerID和金币
      const playersWithPlayerID = await Promise.all(
        playersData.map(async (player: Player) => {
          let updatedPlayer = { ...player };
          
          // 总是尝试获取PlayerID，除非已经有有效的PlayerID
          if (!player.playerId || player.playerId === '0' || player.playerId === '') {
            console.log(`正在获取玩家 ${player.name} (${player.steamId}) 的PlayerID...`);
            const playerID = await getPlayerID(player.steamId);
            console.log(`玩家 ${player.name} 的PlayerID: ${playerID}`);
            updatedPlayer.playerId = playerID;
          } else {
            console.log(`玩家 ${player.name} 已有PlayerID: ${player.playerId}`);
          }
          
          // 获取玩家金币
          console.log(`正在获取玩家 ${player.name} (${player.steamId}) 的金币...`);
          const points = await getPlayerPoints(player.steamId);
          console.log(`玩家 ${player.name} 的金币: ${points}`);
          updatedPlayer.points = points;
          
          return updatedPlayer;
        })
      );
      
      setPlayers(playersWithPlayerID);
      setServerStats(statsRes.data || null);
      setChatMessages(chatRes.data?.messages || []);
      
      message.success('数据加载成功');
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  }, [serverId, getPlayerID, getPlayerPoints, checkServerStatus]);

  // 执行RCON命令
  const executeCommand = async (command: string, params: string[] = [], commandType?: string) => {
    if (!serverId) return;
    
    try {
      const fullCommand = params.length > 0 ? `${command} ${params.join(' ')}` : command;
      console.log(`执行命令: ${fullCommand}`);
      
      const response = await api.post(`/ark-rcon/servers/${serverId}/execute`, {
        command: fullCommand
      });
      
      // 添加到命令历史
      const historyItem: CommandHistory = {
        id: Date.now().toString(),
        command: fullCommand,
        timestamp: new Date().toISOString(),
        success: response.data.success,
        result: response.data.result
      };
      setCommandHistory(prev => [historyItem, ...prev.slice(0, 99)]); // 保留最近100条
      
      if (response.data.success) {
        // 根据命令类型显示不同的反馈
        if (commandType === 'query') {
          // 查询命令显示结果弹窗
          setQueryResult(response.data.result || '查询成功，但无返回结果');
          setQueryResultTitle(`查询结果: ${command}`);
          setQueryResultModalVisible(true);
          message.success(`查询成功: ${command}`);
        } else if (commandType === 'confirm' && command === 'SaveWorld') {
          // 保存世界命令特殊处理
          const result = response.data.result || '';
          if (result.includes('Server received')) {
            message.success('保存世界成功！存档进度已保存。');
          } else {
            message.success(`命令执行成功: ${command}`);
          }
        } else {
          message.success(`命令执行成功: ${command}`);
        }
        
        // 如果是影响数据的命令，重新加载数据
        if (['ListPlayers', 'KickPlayer', 'KillPlayer', 'GivePlayerXP', 'GiveItemNumToPlayer', 'SendMessageToChat'].includes(command)) {
          setTimeout(loadData, 1000);
        }
      } else {
        message.error(`命令执行失败: ${response.data.error || '未知错误'}`);
      }
      
      return response.data;
    } catch (error) {
      console.error('执行命令失败:', error);
      message.error('执行命令失败');
      return { success: false, error: error instanceof Error ? error.message : '未知错误' };
    }
  };

  // 处理键盘事件
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (e.ctrlKey) {
        // Ctrl+Enter 发送聊天消息
        if (chatModalVisible) {
          chatForm.submit();
        }
      } else {
        // Enter 执行命令
        executeCommand(currentCommand);
        setCurrentCommand('');
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistoryIndex < commandHistory.length - 1) {
        const newIndex = commandHistoryIndex + 1;
        setCommandHistoryIndex(newIndex);
        setCurrentCommand(commandHistory[commandHistory.length - 1 - newIndex].command);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (commandHistoryIndex > 0) {
        const newIndex = commandHistoryIndex - 1;
        setCommandHistoryIndex(newIndex);
        setCurrentCommand(commandHistory[commandHistory.length - 1 - newIndex].command);
      } else if (commandHistoryIndex === 0) {
        setCommandHistoryIndex(-1);
        setCurrentCommand('');
      }
    }
  };

  // 初始化（仅第一次加载）
  useEffect(() => {
    // 先检查插件，再加载数据
    checkYouYouNiaoRCONPlugin().then((isInstalled) => {
      if (isInstalled) {
        loadData();
      }
    });
  }, [checkYouYouNiaoRCONPlugin, loadData]);

  // 玩家表格列
  const playerColumns = [
    {
      title: '玩家名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (name: string, record: Player) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{name}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'SteamID',
      dataIndex: 'steamId',
      key: 'steamId',
      width: 150,
      render: (steamId: string) => (
        <Text code style={{ fontSize: '12px' }}>
          {steamId}
        </Text>
      ),
    },
    {
      title: 'PlayerID',
      dataIndex: 'playerId',
      key: 'playerId',
      width: 120,
      render: (playerId: string) => (
        <Text code style={{ fontSize: '12px' }}>
          {playerId && playerId !== '0' ? playerId : '-'}
        </Text>
      ),
    },
    {
      title: '部落名称',
      dataIndex: 'tribeName',
      key: 'tribeName',
      width: 120,
      render: (tribeName: string) => (
        <Tag color={tribeName && tribeName !== '未加入部落' ? 'purple' : 'default'} icon={<TeamOutlined />}>
          {tribeName || '未加入部落'}
        </Tag>
      ),
    },
    {
      title: '位置',
      dataIndex: 'position',
      key: 'position',
      width: 200,
      render: (position: any) => {
        if (!position) return <Text type="secondary">未知</Text>;
        
        if (typeof position === 'string') {
          return <Text code>{position}</Text>;
        }
        
        if (position.formatted) {
          return <Text code>{position.formatted}</Text>;
        }
        
        if (position.x !== undefined && position.y !== undefined && position.z !== undefined) {
          return <Text code>X={position.x.toFixed(2)} Y={position.y.toFixed(2)} Z={position.z.toFixed(2)}</Text>;
        }
        
        return <Text type="secondary">未知</Text>;
      },
    },
    {
      title: '恐龙数量',
      dataIndex: 'dinoCount',
      key: 'dinoCount',
      width: 100,
      render: (dinoCount: number) => (
        <Tag color={dinoCount > 0 ? 'blue' : 'default'} icon={<TrophyOutlined />}>
          {dinoCount || 0}
        </Tag>
      ),
    },
    {
      title: '金币',
      dataIndex: 'points',
      key: 'points',
      width: 150,
      render: (points: number, record: Player) => (
        <Space size="small">
          <Tag color={points > 0 ? 'gold' : 'default'} icon={<DollarOutlined />}>
            {points || 0}
          </Tag>
          <Tooltip title="充值金币">
            <Button 
              size="small" 
              type="primary"
              icon={<PlusOutlined />} 
              onClick={() => {
                setSelectedPlayer(record);
                setAddPointsModalVisible(true);
                addPointsForm.resetFields();
              }}
            />
          </Tooltip>
          <Tooltip title="重置金币">
            <Button 
              size="small" 
              type="default"
              icon={<ReloadOutlined />} 
              onClick={() => {
                setSelectedPlayer(record);
                setSetPointsModalVisible(true);
                setPointsForm.resetFields();
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'isOnline',
      key: 'isOnline',
      width: 100,
      render: (isOnline: boolean) => (
        <Tag color={isOnline ? 'green' : 'red'} icon={isOnline ? <CheckCircleOutlined /> : <CloseOutlined />}>
          {isOnline ? '在线' : '离线'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (text: any, record: Player) => (
        <Space size="small">
          <Tooltip title="传送玩家">
            <Button 
              size="small" 
              icon={<RocketOutlined />} 
              onClick={() => {
                setSelectedPlayer(record);
                setTeleportModalVisible(true);
                teleportForm.setFieldsValue({
                  x: typeof record.position === 'object' && record.position ? record.position.x : 0,
                  y: typeof record.position === 'object' && record.position ? record.position.y : 0,
                  z: typeof record.position === 'object' && record.position ? record.position.z : 0,
                });
              }}
            />
          </Tooltip>
          <Tooltip title="给予物品">
            <Button 
              size="small" 
              icon={<GiftOutlined />} 
              onClick={() => {
                setSelectedPlayer(record);
                setGiveItemModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="给予经验">
            <Button 
              size="small" 
              icon={<TrophyOutlined />} 
              onClick={() => {
                setSelectedPlayer(record);
                setGiveExpModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="给玩家改名">
            <Button 
              size="small" 
              icon={<UserOutlined />} 
              onClick={() => {
                setSelectedPlayer(record);
                setRenameModalVisible(true);
                renameForm.setFieldsValue({
                  newName: record.name
                });
              }}
            />
          </Tooltip>
          <Popconfirm
            title="确定要踢出这个玩家吗？"
            onConfirm={() => executeCommand('KickPlayer', [record.steamId])}
          >
            <Button size="small" danger icon={<CloseOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 服务器统计卡片
  const renderServerStats = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="在线玩家"
            value={serverStats?.onlinePlayers || 0}
            prefix={<UserOutlined />}
            valueStyle={{ color: '#3f8600' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="总驯养数"
            value={serverStats?.totalTames || 0}
            prefix={<RobotOutlined />}
            valueStyle={{ color: '#cf1322' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="未认领恐龙"
            value={serverStats?.unclaimedDinos || 0}
            prefix={<DeleteOutlined />}
            valueStyle={{ color: '#fa8c16' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="总野生数"
            value={serverStats?.totalWilds || 0}
            prefix={<BugOutlined />}
            valueStyle={{ color: '#722ed1' }}
          />
        </Card>
      </Col>
    </Row>
  );

  // 玩家管理面板
  const renderPlayerPanel = () => (
    <Card 
      title="玩家管理" 
      extra={
        <Space>
          <Button 
            type="primary"
            icon={<ReloadOutlined />}
            onClick={loadPlayers}
            loading={loading}
          >
            刷新玩家列表
          </Button>
        </Space>
      }
    >
      <Table
        dataSource={players}
        columns={playerColumns}
        rowKey="steamId"
        pagination={{ pageSize: 10 }}
        loading={loading}
        size="small"
        scroll={{ x: 800 }}
      />
    </Card>
  );

  // 标准ARK命令分类
  const commandCategories: Record<string, {
    title: string;
    icon: React.ReactNode;
    commands: Array<{
      name: string;
      command: string;
      params?: string[];
      description: string;
      special?: string;
      type?: 'query' | 'action' | 'dangerous' | 'modal' | 'confirm';
      icon?: React.ReactNode;
    }>;
  }> = {
    server: {
      title: '服务器管理',
      icon: <SendOutlined />,
      commands: [
        { name: '保存世界', command: 'SaveWorld', description: '保存当前世界状态', type: 'confirm', icon: <SaveOutlined style={{ color: '#52c41a' }} /> },
        { name: '暂停世界', command: 'PlayersOnly', description: '暂停/恢复世界', type: 'action', icon: <SendOutlined style={{ color: '#1890ff' }} /> },
        { name: '设置时间', command: 'SetTimeOfDay', params: ['小时:分钟'], description: '设置游戏时间', type: 'modal', icon: <SendOutlined style={{ color: '#fa8c16' }} /> },
        { name: '设置速度', command: 'Slomo', params: ['速度值'], description: '设置游戏速度', type: 'modal', icon: <SendOutlined style={{ color: '#722ed1' }} /> },
        { name: '显示公告', command: 'ShowMessageOfTheDay', description: '显示当前服务器设定的固定公告内容', type: 'query', icon: <MessageOutlined style={{ color: '#13c2c2' }} /> },
        { name: '发送公告', command: 'SetMessageOfTheDay', params: ['消息内容'], description: '从公告栏发送消息给所有玩家', type: 'modal', icon: <MessageOutlined style={{ color: '#fa541c' }} /> },
        { name: '获取在线玩家数', command: 'GetOnlineNum', description: '获取当前在线玩家数量', type: 'query', icon: <UserOutlined style={{ color: '#52c41a' }} /> },
        { name: '获取驯养总数', command: 'GetTotalTames', description: '获取服务器驯养恐龙总数', type: 'query', icon: <TrophyOutlined style={{ color: '#1890ff' }} /> },
        { name: '获取野生总数', command: 'GetTotalWilds', description: '获取服务器野生恐龙总数', type: 'query', icon: <TrophyOutlined style={{ color: '#fa8c16' }} /> }
      ]
    },
    player: {
      title: '玩家管理',
      icon: <UserOutlined />,
      commands: [
        { name: '列出玩家', command: 'ListPlayers', description: '列出所有在线玩家', type: 'query', icon: <UserOutlined style={{ color: '#1890ff' }} /> },
        { name: '获取玩家ID', command: 'GetPlayerIDForSteamID', params: ['SteamID'], description: '根据SteamID获取PlayerID', type: 'modal', icon: <UserOutlined style={{ color: '#52c41a' }} /> },
        { name: '获取玩家名称', command: 'GetPlayerName', params: ['SteamID'], description: '根据SteamID获取玩家名称', type: 'modal', icon: <UserOutlined style={{ color: '#fa8c16' }} /> },
        { name: '设置玩家名称', command: 'SetPlayerName', params: ['SteamID', '新名称'], description: '给玩家设置新名称', type: 'modal', icon: <UserOutlined style={{ color: '#722ed1' }} /> },
        { name: '踢出玩家', command: 'KickPlayer', params: ['SteamID'], description: '踢出指定玩家', type: 'dangerous', icon: <UserOutlined style={{ color: '#f5222d' }} /> },
        { name: '杀死玩家', command: 'KillPlayer', params: ['SteamID'], description: '杀死指定玩家', type: 'dangerous', icon: <UserOutlined style={{ color: '#eb2f96' }} /> },
        { name: '封禁玩家', command: 'BanPlayer', params: ['SteamID'], description: '封禁指定玩家', type: 'dangerous', icon: <UserOutlined style={{ color: '#fa541c' }} /> },
        { name: '解封玩家', command: 'UnBanPlayer', params: ['SteamID'], description: '解封指定玩家', type: 'modal', icon: <UserOutlined style={{ color: '#13c2c2' }} /> },
        { name: '允许玩家加入', command: 'AllowPlayerToJoinNoCheck', params: ['SteamID'], description: '允许玩家加入', type: 'modal', icon: <UserOutlined style={{ color: '#52c41a' }} /> },
        { name: '禁止玩家加入', command: 'DisallowPlayerToJoinNoCheck', params: ['SteamID'], description: '禁止玩家加入', type: 'modal', icon: <UserOutlined style={{ color: '#f5222d' }} /> },
        { name: '查询玩家金币', command: 'GetPlayerPoints', params: ['SteamID'], description: '查询指定玩家的金币数量', type: 'modal', icon: <DollarOutlined style={{ color: '#faad14' }} /> },
        { name: '充值玩家金币', command: 'AddPoints', params: ['SteamID', '金额'], description: '给玩家充值金币', type: 'modal', icon: <PlusOutlined style={{ color: '#52c41a' }} /> },
        { name: '重置玩家金币', command: 'SetPoints', params: ['SteamID', '金额'], description: '重置玩家金币数量', type: 'modal', icon: <ReloadOutlined style={{ color: '#1890ff' }} /> }
      ]
    },
    items: {
      title: '物品管理',
      icon: <GiftOutlined />,
      commands: [
        { name: '给予物品', command: 'GiveItemToSteamId', params: ['SteamID', '物品蓝图路径', '数量', '品质', '强制蓝图'], description: '给予玩家物品', type: 'modal', icon: <GiftOutlined style={{ color: '#52c41a' }} /> },
        { name: '全服奖励物品', command: 'GiveItemToAll', params: ['物品蓝图路径', '数量', '品质', '强制蓝图'], description: '发送指定物品给所有玩家', type: 'modal', icon: <GiftOutlined style={{ color: '#1890ff' }} /> },
        { name: '给予经验', command: 'GivePlayerXP', params: ['SteamID', '经验值', '部落分享(0/1)', '阻止部落分享(0/1)'], description: '给予玩家经验', type: 'modal', icon: <TrophyOutlined style={{ color: '#fa8c16' }} /> }
      ]
    },
    chat: {
      title: '聊天系统',
      icon: <MessageOutlined />,
      commands: [
        { name: '服务器聊天', command: 'SendMessageToChat', params: ['消息内容', '发送者'], description: '发送服务器聊天消息', type: 'modal', icon: <MessageOutlined style={{ color: '#1890ff' }} /> },
        { name: '获取聊天', command: 'getchat', description: '获取聊天记录', type: 'query', icon: <MessageOutlined style={{ color: '#52c41a' }} /> },
        { name: '发送给玩家', command: 'ServerChatToPlayer', params: ['SteamID', '消息内容'], description: '发送消息给指定玩家', type: 'modal', icon: <MessageOutlined style={{ color: '#fa8c16' }} /> }
      ]
    },
    teleport: {
      title: '传送系统',
      icon: <RocketOutlined />,
      commands: [
        { name: '获取玩家位置', command: 'GetPlayerPos', params: ['SteamID'], description: '获取指定玩家的位置坐标', type: 'modal', icon: <RocketOutlined style={{ color: '#52c41a' }} /> },
        { name: '传送到玩家', command: 'TeleportToPlayer', params: ['被传送玩家 SteamID', '目标玩家 SteamID'], description: '传送一个玩家到另一个玩家位置', type: 'modal', icon: <RocketOutlined style={{ color: '#1890ff' }} /> },
        { name: '传送到坐标', command: 'Teleport', params: ['X', 'Y', 'Z'], description: '传送到指定坐标', type: 'modal', icon: <RocketOutlined style={{ color: '#fa8c16' }} /> },
        { name: '设置玩家位置', command: 'SetPlayerPos', params: ['SteamID', 'X', 'Y', 'Z'], description: '设置玩家位置', type: 'modal', icon: <RocketOutlined style={{ color: '#722ed1' }} /> }
      ]
    },
    destroy: {
      title: '销毁系统',
      icon: <DeleteOutlined />,
      commands: [
        { name: '销毁所有', command: 'DestroyAll', params: ['类型'], description: '销毁所有指定类型的对象', type: 'dangerous', icon: <DeleteOutlined style={{ color: '#f5222d' }} /> },
        { name: '销毁所有敌人', command: 'DestroyAllEnemies', description: '销毁所有敌人', type: 'dangerous', icon: <DeleteOutlined style={{ color: '#eb2f96' }} /> },
        { name: '销毁建筑', command: 'DestroyStructures', description: '销毁所有建筑', type: 'dangerous', icon: <DeleteOutlined style={{ color: '#fa541c' }} /> },
        { name: '销毁野生恐龙', command: 'DestroyWildDinos', description: '销毁所有野生恐龙', type: 'dangerous', icon: <DeleteOutlined style={{ color: '#fa8c16' }} /> },
        { name: '杀死未认领恐龙', command: 'KillUnclaimedDinos', description: '杀死所有未认领的恐龙（无主的恐龙）', type: 'dangerous', icon: <DeleteOutlined style={{ color: '#722ed1' }} /> }
      ]
    },
    tribe: {
      title: '部落管理',
      icon: <TeamOutlined />,
      commands: [
        { name: '重命名部落', command: 'RenameTribe', params: ['TribeID', '新名称'], description: '重命名部落', type: 'modal', icon: <TeamOutlined style={{ color: '#722ed1' }} /> },
        { name: '强制加入部落', command: 'ForcePlayerToJoinTribe', params: ['SteamID', 'TribeID'], description: '强制玩家加入部落', type: 'modal', icon: <TeamOutlined style={{ color: '#1890ff' }} /> },
        { name: '设置部落管理员', command: 'MakeTribeAdmin', params: ['SteamID'], description: '设置部落管理员', type: 'modal', icon: <TeamOutlined style={{ color: '#52c41a' }} /> },
        { name: '移除部落管理员', command: 'RemoveTribeAdmin', params: ['SteamID'], description: '移除部落管理员', type: 'modal', icon: <TeamOutlined style={{ color: '#f5222d' }} /> }
      ]
    },
    weather: {
      title: '天气控制',
      icon: <CloudOutlined />,
      commands: [
        { name: '设置天气', command: 'CE', params: ['天气类型'], description: '使用CE命令控制天气环境', special: 'weather', type: 'modal', icon: <CloudOutlined style={{ color: '#1890ff' }} /> }
      ]
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <ConsoleSqlOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          ARKRcon管理控制台
          <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'normal', marginLeft: '12px',color: '#1890ff' }}>
            所有操作即时生效，无需重启服务器
          </Text>
        </Title>
        <Paragraph type="secondary">
          悠悠鸟游戏专门对ARK控制台命令进行开发的控制台管理工具，支持服务器管理、玩家管理、物品管理、聊天系统、传送系统、销毁系统、部落管理、天气控制等功能。
        </Paragraph>
      </div>

      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={[
          {
            key: 'overview',
            label: <span><BarChartOutlined />概览</span>,
            children: (
              <>
                {renderServerStats()}
                <div style={{ marginTop: '24px' }}>
                  <Card title="快速操作" size="small">
                    <Space wrap>
                      <Button 
                        type="primary"
                        icon={<GiftOutlined />}
                        onClick={() => {
                          setSelectedCommand({
                            name: '全服奖励物品',
                            command: 'GiveItemToAll',
                            params: ['BlueprintPath', 'Quantity', 'Quality', 'ForceBlueprint']
                          });
                          setCommandModalVisible(true);
                        }}
                      >
                        全服奖励物品
                      </Button>
                      <Button 
                        type="primary"
                        icon={<RocketOutlined />}
                        onClick={() => {
                          Modal.confirm({
                            title: '传送所有玩家',
                            content: '确定要传送所有玩家到指定位置吗？',
                            onOk: () => {
                              setSelectedCommand({
                                name: '传送所有玩家',
                                command: 'TeleportAllPlayers',
                                params: ['X', 'Y', 'Z']
                              });
                              setCommandModalVisible(true);
                            }
                          });
                        }}
                      >
                        传送所有玩家
                      </Button>
                      <Button 
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={() => {
                          setSelectedCommand({
                            name: '保存世界',
                            command: 'SaveWorld',
                            params: []
                          });
                          setCommandModalVisible(true);
                        }}
                      >
                        保存世界
                      </Button>
                      <Button 
                        type="primary"
                        icon={<MessageOutlined />}
                        onClick={() => {
                          setSelectedCommand({
                            name: '显示公告',
                            command: 'ShowMessageOfTheDay',
                            params: []
                          });
                          setCommandModalVisible(true);
                        }}
                      >
                        显示公告
                      </Button>
                      <Button 
                        type="primary"
                        icon={<ClockCircleOutlined />}
                        onClick={() => {
                          setSelectedCommand({
                            name: '设置时间',
                            command: 'SetTimeOfDay',
                            params: ['Time']
                          });
                          setCommandModalVisible(true);
                        }}
                      >
                        设置时间
                      </Button>
                      <Button 
                        type="primary"
                        icon={<GiftOutlined />}
                        onClick={() => {
                          setSelectedCommand({
                            name: '给予物品',
                            command: 'GiveItemToSteamId',
                            params: ['SteamID', 'BlueprintPath', 'Quantity', 'Quality', 'ForceBlueprint']
                          });
                          setCommandModalVisible(true);
                        }}
                      >
                        给予物品
                      </Button>
                      <Button 
                        type="primary"
                        icon={<TrophyOutlined />}
                        onClick={() => {
                          setSelectedCommand({
                            name: '给予经验',
                            command: 'GivePlayerXP',
                            params: ['SteamID', 'Experience', 'TribeShare(0/1)', 'PreventTribeShare(0/1)']
                          });
                          setCommandModalVisible(true);
                        }}
                      >
                        给予经验
                      </Button>
                      <Button 
                        type="primary"
                        icon={<FireOutlined />}
                        onClick={() => {
                          setSelectedCommand({
                            name: '销毁野生恐龙',
                            command: 'DestroyWildDinos',
                            params: []
                          });
                          setCommandModalVisible(true);
                        }}
                      >
                        销毁野生恐龙
                      </Button>
                      <Button 
                        type="primary"
                        icon={<CloudOutlined />}
                        onClick={() => {
                          setWeatherModalVisible(true);
                        }}
                      >
                        设置天气
                      </Button>
                    </Space>
                  </Card>
                </div>
                
                
                <div style={{ marginTop: '24px' }}>
                  {renderPlayerPanel()}
                </div>
              </>
            )
          },
          {
            key: 'players',
            label: <span><UserOutlined />玩家管理</span>,
            children: renderPlayerPanel()
          },
          {
            key: 'commands',
            label: <span><CodeOutlined />命令控制台</span>,
            children: (
              <Card title="命令控制台">
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  {/* 命令输入区域 */}
                  <Card title="命令输入" size="small">
                    <Space.Compact style={{ width: '100%' }}>
                      <Input
                        ref={commandInputRef}
                        placeholder="输入ARK控制台命令..."
                        value={currentCommand}
                        onChange={(e) => setCurrentCommand(e.target.value)}
                        onKeyDown={handleKeyPress}
                        style={{ fontFamily: 'monospace' }}
                      />
                      <Button 
                        type="primary" 
                        icon={<SendOutlined />}
                        onClick={() => {
                          executeCommand(currentCommand);
                          setCurrentCommand('');
                        }}
                        disabled={!currentCommand.trim()}
                      >
                        执行
                      </Button>
                    </Space.Compact>
                    <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                      提示: 按 Enter 执行命令，按 ↑↓ 浏览历史记录
                    </div>
                  </Card>


                  {/* 历史操作区域 */}
                  <Card title="历史操作" size="small">
                    <div style={{ height: '400px', overflowY: 'auto' }}>
                      <List
                        size="small"
                        dataSource={commandHistory.slice(0, 20)}
                        renderItem={(item) => (
                          <List.Item>
                            <div style={{ width: '100%' }}>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {new Date(item.timestamp).toLocaleTimeString()}
                              </div>
                              <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                {item.command}
                              </div>
                              {item.result && (
                                <div style={{ fontSize: '11px', color: item.success ? '#52c41a' : '#ff4d4f' }}>
                                  {item.result}
                                </div>
                              )}
                            </div>
                          </List.Item>
                        )}
                      />
                    </div>
                  </Card>
                </Space>
              </Card>
            )
          },
          {
            key: 'categories',
            label: <span><BookOutlined />功能分类</span>,
            children: (
              <div>
                {Object.entries(commandCategories).map(([key, category]) => (
                  <Card 
                    key={key}
                    title={
                      <Space>
                        {category.icon}
                        {category.title}
                      </Space>
                    }
                    style={{ marginBottom: '16px' }}
                  >
                    <Row gutter={[8, 8]}>
                      {category.commands.map((cmd, index) => (
                        <Col xs={24} sm={12} md={8} lg={6} key={index}>
                          <Card 
                            size="small" 
                            hoverable 
                            onClick={() => {
                              if (cmd.special === 'weather') {
                                // 天气命令特殊处理
                                setWeatherModalVisible(true);
                                weatherForm.resetFields();
                              } else if (cmd.type === 'dangerous') {
                                // 危险操作需要确认
                                Modal.confirm({
                                  title: '危险操作确认',
                                  content: `确定要执行 "${cmd.name}" 吗？此操作可能对服务器造成不可逆的影响！`,
                                  okText: '确认执行',
                                  cancelText: '取消',
                                  okType: 'danger',
                                  onOk: () => {
                                    if (cmd.params && cmd.params.length > 0) {
                                      setSelectedCommand(cmd);
                                      setCommandModalVisible(true);
                                      commandForm.resetFields();
                                    } else {
                                      executeCommand(cmd.command, [], cmd.type);
                                    }
                                  }
                                });
                              } else if (cmd.type === 'confirm') {
                                // 需要确认的操作
                                Modal.confirm({
                                  title: '操作确认',
                                  content: `确定要执行 "${cmd.name}" 吗？${cmd.name === '保存世界' ? '将保存当前存档进度。' : ''}`,
                                  okText: '确认执行',
                                  cancelText: '取消',
                                  onOk: () => {
                                    if (cmd.params && cmd.params.length > 0) {
                                      setSelectedCommand(cmd);
                                      setCommandModalVisible(true);
                                      commandForm.resetFields();
                                    } else {
                                      executeCommand(cmd.command, [], cmd.type);
                                    }
                                  }
                                });
                              } else if (cmd.params && cmd.params.length > 0) {
                                setSelectedCommand(cmd);
                                setCommandModalVisible(true);
                                commandForm.resetFields();
                              } else {
                                executeCommand(cmd.command, [], cmd.type);
                              }
                            }}
                          >
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ marginBottom: '8px', fontSize: '20px' }}>
                                {cmd.icon}
                              </div>
                              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{cmd.name}</div>
                              <div style={{ fontSize: '12px', color: '#666' }}>{cmd.description}</div>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Card>
                ))}
              </div>
            )
          },
          {
            key: 'chat',
            label: <span><MessageOutlined />聊天系统</span>,
            children: (
              <Card 
                title="聊天系统"
                extra={
                  <Button 
                    icon={<ReloadOutlined />}
                    onClick={loadChatMessages}
                    size="small"
                  >
                    刷新聊天记录
                  </Button>
                }
              >
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card 
                      title="聊天记录" 
                      size="small"
                      extra={
                        <Tooltip title="GetChat命令只返回最新的聊天缓冲区，不是完整历史记录">
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            <MessageOutlined /> 缓冲区
                          </Text>
                        </Tooltip>
                      }
                    >
                      <div style={{ height: '300px', overflowY: 'auto', border: '1px solid #d9d9d9', padding: '8px' }}>
                        {chatMessages.map((msg) => (
                          <div key={msg.id} style={{ marginBottom: '4px' }}>
                            <Text type="secondary">[{msg.time || new Date(msg.timestamp).toLocaleTimeString()}]</Text>
                            <Text strong style={{ marginLeft: '8px' }}>{msg.player}:</Text>
                            <Text style={{ marginLeft: '4px' }}>{msg.message}</Text>
                          </div>
                        ))}
                        {loading && <Spin size="small" />}
                      </div>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title="发送消息" size="small">
                      <Form
                        form={chatForm}
                        layout="vertical"
                        onFinish={async (values) => {
                          const sender = values.sender || 'admin';
                          await executeCommand('SendMessageToChat', [`'${values.message}'`, sender]);
                          setChatModalVisible(false);
                        }}
                      >
                        <Form.Item
                          name="sender"
                          label="发送者名称"
                          initialValue="admin"
                        >
                          <Input placeholder="发送者名称，默认为admin" />
                        </Form.Item>
                        <Form.Item
                          name="message"
                          label="消息内容"
                          rules={[{ required: true, message: '请输入消息内容' }]}
                        >
                          <Input.TextArea rows={4} placeholder="输入要发送的消息" />
                        </Form.Item>
                        <Form.Item>
                          <Space>
                            <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
                              发送消息
                            </Button>
                            <Button onClick={() => setChatModalVisible(false)}>
                              取消
                            </Button>
                          </Space>
                        </Form.Item>
                      </Form>
                    </Card>
                  </Col>
                </Row>
              </Card>
            )
          }
        ]}
      />

      {/* 命令执行模态框 */}
      <Modal
        title={`执行命令: ${selectedCommand?.name}`}
        open={commandModalVisible}
        onCancel={() => {
          setCommandModalVisible(false);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={commandForm}
          layout="vertical"
          onFinish={async (values) => {
            const params = selectedCommand?.params?.map((param: string) => values[param]) || [];
            await executeCommand(selectedCommand?.command, params, selectedCommand?.type);
            setCommandModalVisible(false);
          }}
        >
          {(() => {
            // 定义参数标签函数
            const getParamLabel = (param: string) => {
              switch (param) {
                case 'SteamID':
                  return 'SteamID';
                case 'Quantity':
                  return '数量';
                case 'Quality':
                  return '品质';
                case 'ForceBlueprint':
                  return '强制蓝图';
                case 'Experience':
                  return '经验值';
                case 'TribeShare(0/1)':
                  return '部落分享(0/1)';
                case 'PreventTribeShare(0/1)':
                  return '阻止部落分享(0/1)';
                case 'TribeID':
                  return '部落ID';
                case '新名称':
                  return '新名称';
                case '消息内容':
                  return '消息内容';
                case '发送者':
                  return '发送者';
                case 'X':
                  return 'X坐标';
                case 'Y':
                  return 'Y坐标';
                case 'Z':
                  return 'Z坐标';
                case '被传送玩家 SteamID':
                  return '被传送玩家 SteamID';
                case '目标玩家 SteamID':
                  return '目标玩家 SteamID';
                case '金额':
                  return '金币数量';
                default:
                  return param;
              }
            };

            // 定义默认值函数
            const getDefaultValue = (param: string) => {
              switch (param) {
                case 'Quantity':
                  return '1';
                case 'Quality':
                  return '1';
                case 'ForceBlueprint':
                  return '0';
                case '金额':
                  return '1000000';
                default:
                  return undefined;
              }
            };

            return selectedCommand?.params?.map((param: string, index: number) => {
              // 如果是BlueprintPath参数，使用新的ARK物品选择器
              if (param === 'BlueprintPath') {
                return (
                  <Form.Item
                    key={index}
                    name={param}
                    label="物品蓝图路径"
                    rules={[{ required: true, message: '请选择或输入物品蓝图路径' }]}
                  >
                    <ARKItemSelector
                      placeholder="选择物品或输入蓝图路径"
                      onChange={(value) => {
                        commandForm.setFieldsValue({ BlueprintPath: value });
                      }}
                    />
                  </Form.Item>
                );
              }
              
              // 如果是SteamID参数，使用玩家选择器
              if (param === 'SteamID' || param.includes('SteamID')) {
                return (
                  <Form.Item
                    key={index}
                    name={param}
                    label={getParamLabel(param)}
                    rules={[{ required: true, message: `请选择或输入${getParamLabel(param)}` }]}
                  >
                    <div>
                      <Tag color="blue" style={{ marginBottom: 8, fontSize: '12px' }}>
                        点击输入框可快速选择在线玩家
                      </Tag>
                      <PlayerSelector
                        placeholder="选择玩家或输入SteamID"
                        serverId={serverId}
                        onChange={(value) => {
                          commandForm.setFieldsValue({ [param]: value });
                        }}
                      />
                    </div>
                  </Form.Item>
                );
              }

              // 如果是金额参数，使用数字输入框
              if (param === '金额') {
                return (
                  <Form.Item
                    key={index}
                    name={param}
                    label={getParamLabel(param)}
                    rules={[{ required: true, message: `请输入${getParamLabel(param)}` }]}
                    initialValue={getDefaultValue(param)}
                  >
                    <InputNumber 
                      style={{ width: '100%' }} 
                      min={0} 
                      max={999999999}
                      placeholder={`请输入${getParamLabel(param)}`}
                      addonAfter="金币"
                    />
                  </Form.Item>
                );
              }

              // 其他参数使用普通输入框

              return (
                <Form.Item
                  key={index}
                  name={param}
                  label={getParamLabel(param)}
                  rules={[{ required: true, message: `请输入${getParamLabel(param)}` }]}
                  initialValue={getDefaultValue(param)}
                >
                  <Input placeholder={`请输入${getParamLabel(param)}`} />
                </Form.Item>
              );
            });
          })()}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
                执行命令
              </Button>
              <Button onClick={() => setCommandModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 传送玩家模态框 */}
      <Modal
        title={`传送玩家: ${selectedPlayer?.name}`}
        open={teleportModalVisible}
        onCancel={() => setTeleportModalVisible(false)}
        footer={null}
      >
        <Form
          form={teleportForm}
          layout="vertical"
          onFinish={async (values) => {
            await executeCommand('SetPlayerPos', [
              selectedPlayer?.steamId || '',
              values.x.toString(),
              values.y.toString(),
              values.z.toString()
            ]);
            setTeleportModalVisible(false);
          }}
        >
          <Form.Item
            name="x"
            label="X坐标"
            rules={[{ required: true, message: '请输入X坐标' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="y"
            label="Y坐标"
            rules={[{ required: true, message: '请输入Y坐标' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="z"
            label="Z坐标"
            rules={[{ required: true, message: '请输入Z坐标' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<RocketOutlined />}>
                传送玩家
              </Button>
              <Button onClick={() => setTeleportModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 给予物品模态框 */}
      <Modal
        title="给予物品"
        open={giveItemModalVisible}
        onCancel={() => setGiveItemModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={giveItemForm}
          layout="vertical"
          onFinish={async (values) => {
            await executeCommand('GiveItemToSteamId', [
              selectedPlayer?.steamId || '',
              values.itemBlueprint,
              values.quantity.toString(),
              values.quality || '1',
              values.forceBlueprint || '0'
            ]);
            setGiveItemModalVisible(false);
          }}
        >
          <Form.Item
            name="itemBlueprint"
            label="物品蓝图路径"
            rules={[{ required: true, message: '请选择或输入物品蓝图路径' }]}
          >
            <ARKItemSelector
              placeholder="选择物品或输入蓝图路径"
              onChange={(value) => {
                giveItemForm.setFieldsValue({ itemBlueprint: value });
              }}
            />
          </Form.Item>
          <Form.Item
            name="quantity"
            label="数量"
            rules={[{ required: true, message: '请输入数量' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="quality"
            label="品质"
          >
            <InputNumber min={1} max={5} defaultValue={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="forceBlueprint"
            label="强制蓝图"
          >
            <Select defaultValue="0">
              <Option value="1">是</Option>
              <Option value="0">否</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<GiftOutlined />}>
                给予物品
              </Button>
              <Button onClick={() => setGiveItemModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 给予经验模态框 */}
      <Modal
        title="给予经验"
        open={giveExpModalVisible}
        onCancel={() => setGiveExpModalVisible(false)}
        footer={null}
      >
        <Form
          form={giveExpForm}
          layout="vertical"
          onFinish={async (values) => {
            await executeCommand('GivePlayerXP', [
              selectedPlayer?.steamId || '',
              values.amount.toString(),
              values.tribeShare ? '1' : '0',
              values.preventTribeShare ? '1' : '0'
            ]);
            setGiveExpModalVisible(false);
          }}
        >
          <Form.Item
            name="amount"
            label="经验数量"
            rules={[{ required: true, message: '请输入经验数量' }]}
          >
            <InputNumber min={1} max={1000000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="tribeShare"
            label="部落分享"
            valuePropName="checked"
            getValueFromEvent={(e) => e.target.checked}
          >
            <Select defaultValue={0}>
              <Option value={1}>是</Option>
              <Option value={0}>否</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="preventTribeShare"
            label="阻止部落分享"
            valuePropName="checked"
            getValueFromEvent={(e) => e.target.checked}
          >
            <Select defaultValue={0}>
              <Option value={1}>是</Option>
              <Option value={0}>否</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<TrophyOutlined />}>
                给予经验
              </Button>
              <Button onClick={() => setGiveExpModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 天气设置模态框 */}
      <Modal
        title="天气控制"
        open={weatherModalVisible}
        onCancel={() => setWeatherModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={weatherForm}
          layout="vertical"
          onFinish={async (values) => {
            const weatherCommand = values.weatherType;
            await executeCommand('CE', [weatherCommand]);
            setWeatherModalVisible(false);
          }}
        >
          <Form.Item
            name="map"
            label="地图类型"
            rules={[{ required: true, message: '请选择地图类型' }]}
          >
            <Select placeholder="选择地图类型" onChange={(value) => {
              // 根据地图类型更新天气选项
              weatherForm.setFieldsValue({ weatherType: undefined });
            }}>
              <Option value="island">The Island (岛屿)</Option>
              <Option value="scorched">Scorched Earth (焦土)</Option>
              <Option value="ragnarok">Ragnarok (诸神黄昏)</Option>
              <Option value="aberration">Aberration (畸变)</Option>
              <Option value="extinction">Extinction (灭绝)</Option>
              <Option value="valguero">Valguero (瓦尔盖罗)</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.map !== currentValues.map}
          >
            {({ getFieldValue }) => {
              const mapType = getFieldValue('map');
              let weatherOptions: Array<{ value: string; label: string }> = [];
              
              switch (mapType) {
                case 'island':
                  weatherOptions = [
                    { value: 'heatwave', label: '热浪' },
                    { value: 'coldfront', label: '冷锋' },
                    { value: 'makeitrain', label: '下雨' },
                    { value: 'fogitup', label: '起雾' }
                  ];
                  break;
                case 'scorched':
                  weatherOptions = [
                    { value: 'start_superheat', label: '开始超级热浪' },
                    { value: 'stop_superheat', label: '停止超级热浪' },
                    { value: 'start_sandstorm', label: '开始沙尘暴' },
                    { value: 'stop_sandstorm', label: '停止沙尘暴' },
                    { value: 'start_electricalstorm', label: '开始电气风暴' },
                    { value: 'stop_electricalstorm', label: '停止电气风暴' },
                    { value: 'start_rain', label: '开始下雨' },
                    { value: 'stop_rain', label: '停止下雨' }
                  ];
                  break;
                case 'ragnarok':
                  weatherOptions = [
                    { value: '"start rain"', label: '开始下雨' },
                    { value: '"stop rain"', label: '停止下雨' },
                    { value: '"start electricalstorm"', label: '开始电气风暴' },
                    { value: '"stop electricalstorm"', label: '停止电气风暴' },
                    { value: '"start sandstorm"', label: '开始沙尘暴' },
                    { value: '"stop sandstorm"', label: '停止沙尘暴' },
                    { value: '"start superheat"', label: '开始超级热浪' },
                    { value: '"stop superheat"', label: '停止超级热浪' },
                    { value: 'Start_Volcano', label: '启动火山' }
                  ];
                  break;
                case 'aberration':
                  weatherOptions = [
                    { value: 'startquake', label: '开始地震' },
                    { value: 'stopquake', label: '停止地震' }
                  ];
                  break;
                case 'extinction':
                  weatherOptions = [
                    { value: '"start meteors"', label: '开始陨石雨' }
                  ];
                  break;
                case 'valguero':
                  weatherOptions = [
                    { value: 'SpawnRainbow', label: '生成彩虹' },
                    { value: 'TestNorthernLights', label: '测试北极光' }
                  ];
                  break;
                default:
                  weatherOptions = [];
              }
              
              return (
                <Form.Item
                  name="weatherType"
                  label="天气类型"
                  rules={[{ required: true, message: '请选择天气类型' }]}
                >
                  <Select placeholder="选择天气类型" disabled={!mapType}>
                    {weatherOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              );
            }}
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<CloudOutlined />}>
                设置天气
              </Button>
              <Button onClick={() => setWeatherModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 玩家改名模态框 */}
      <Modal
        title="给玩家改名"
        open={renameModalVisible}
        onCancel={() => setRenameModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={renameForm}
          layout="vertical"
          onFinish={async (values) => {
            if (selectedPlayer) {
              await executeCommand('SetPlayerName', [selectedPlayer.steamId, values.newName]);
              setRenameModalVisible(false);
              // 刷新玩家列表以显示新名称
              loadPlayers();
            }
          }}
        >
          <Form.Item
            name="newName"
            label="新名称"
            rules={[
              { required: true, message: '请输入新名称' },
              { min: 1, max: 50, message: '名称长度应在1-50个字符之间' },
              { 
                pattern: /^[a-zA-Z0-9_\-\s]+$/, 
                message: '建议使用英文字母、数字、下划线、连字符和空格，避免中文字符' 
              }
            ]}
          >
            <Input 
              placeholder="请输入新的玩家名称（建议使用英文）" 
              addonAfter={
                <Tooltip title="RCON协议对中文字符支持有限，建议使用英文字符">
                  <span style={{ color: '#ff4d4f', fontSize: '12px' }}>⚠️</span>
                </Tooltip>
              }
            />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<UserOutlined />}>
                确认改名
              </Button>
              <Button onClick={() => setRenameModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 充值金币模态框 */}
      <Modal
        title={`充值金币: ${selectedPlayer?.name}`}
        open={addPointsModalVisible}
        onCancel={() => setAddPointsModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={addPointsForm}
          layout="vertical"
          onFinish={async (values) => {
            if (selectedPlayer) {
              await executeCommand('AddPoints', [selectedPlayer.steamId, values.amount.toString()]);
              setAddPointsModalVisible(false);
              // 刷新玩家列表以显示新的金币数量
              loadPlayers();
            }
          }}
        >
          <Form.Item
            name="amount"
            label="充值金额"
            rules={[
              { required: true, message: '请输入充值金额' },
              { type: 'number', min: 1, message: '充值金额必须大于0' }
            ]}
          >
            <InputNumber 
              style={{ width: '100%' }} 
              min={1} 
              max={999999999}
              placeholder="请输入要充值的金币数量"
              addonAfter="金币"
            />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                确认充值
              </Button>
              <Button onClick={() => setAddPointsModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 重置金币模态框 */}
      <Modal
        title={`重置金币: ${selectedPlayer?.name}`}
        open={setPointsModalVisible}
        onCancel={() => setSetPointsModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={setPointsForm}
          layout="vertical"
          onFinish={async (values) => {
            if (selectedPlayer) {
              await executeCommand('SetPoints', [selectedPlayer.steamId, values.amount.toString()]);
              setSetPointsModalVisible(false);
              // 刷新玩家列表以显示新的金币数量
              loadPlayers();
            }
          }}
        >
          <Form.Item
            name="amount"
            label="重置金额"
            rules={[
              { required: true, message: '请输入重置金额' },
              { type: 'number', min: 0, message: '重置金额不能小于0' }
            ]}
          >
            <InputNumber 
              style={{ width: '100%' }} 
              min={0} 
              max={999999999}
              placeholder="请输入要设置的金币数量"
              addonAfter="金币"
            />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<ReloadOutlined />}>
                确认重置
              </Button>
              <Button onClick={() => setSetPointsModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 服务器状态弹窗 */}
      <Modal
        title="服务器未启动"
        open={serverStatusModalVisible}
        onCancel={() => setServerStatusModalVisible(false)}
        footer={[
          <Button key="refresh" type="primary" onClick={() => {
            setServerStatusModalVisible(false);
            checkServerStatus();
          }}>
            重新检查状态
          </Button>,
          <Button key="close" onClick={() => setServerStatusModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', color: '#ff4d4f' }}>
            服务器当前状态: {serverStatus}
          </div>
          <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
            RCON控制台功能需要服务器处于<strong style={{ color: '#52c41a' }}>运行中</strong>状态才能使用。
            <br />
            请先启动服务器，然后再使用本页面的功能。
          </div>
        </div>
      </Modal>

      {/* 查询结果弹窗 */}
      <Modal
        title={queryResultTitle}
        open={queryResultModalVisible}
        onCancel={() => setQueryResultModalVisible(false)}
        footer={[
          <Button key="copy" onClick={() => {
            navigator.clipboard.writeText(queryResult);
            message.success('结果已复制到剪贴板');
          }}>
            复制结果
          </Button>,
          <Button key="close" type="primary" onClick={() => setQueryResultModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#f6ffed', 
          border: '1px solid #b7eb8f',
          borderRadius: '6px',
          fontFamily: 'monospace',
          fontSize: '14px',
          whiteSpace: 'pre-wrap',
          maxHeight: '400px',
          overflowY: 'auto',
          lineHeight: '1.5'
        }}>
          {queryResult}
        </div>
      </Modal>

      {/* 插件检查警告弹窗 */}
      <Modal
        title="插件检查"
        open={pluginCheckModalVisible}
        onCancel={() => setPluginCheckModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPluginCheckModalVisible(false)}>
            稍后安装
          </Button>,
          <Button key="install" type="primary" onClick={() => {
            setPluginCheckModalVisible(false);
            navigateToPluginManagement();
          }}>
            前往安装
          </Button>
        ]}
        width={500}
        centered
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', color: '#fa8c16' }}>
            请先安装悠悠鸟RCON插件
          </div>
          <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
            悠悠鸟RCON插件是ARK控制台管理工具的核心组件，提供服务器管理、玩家管理、物品管理、聊天系统、传送系统、销毁系统、部落管理、天气控制等功能。
            <br /><br />
            为了获得更好的功能支持，请先安装此插件。
          </div>
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#fff7e6', 
            borderRadius: '6px', 
            border: '1px solid #ffd591',
            marginBottom: '20px'
          }}>
            <p style={{ fontSize: '12px', color: '#d46b08', margin: 0 }}>
              💡 提示：安装插件后需要重启服务器才能生效
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ARKRCONConsole;
